import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  getPosEntityStopwatchLoggingId,
  getStopwatchGapsForActivePosEntityPageLoad,
  StopwatchLoggingEventName,
  useStopwatchLoggingContext,
} from 'src/contexts/StopwatchLoggingContext';
import { SaleDeeplinkQueryParam } from 'src/utils/constants/constants';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import {
  ActionOutboxEntityType,
  Feature,
  Tag,
  TagClient,
  TagsValueType,
} from 'src/WebApiController';

import { useUserHasFeature } from './useUserHasFeature';

export function useTagsForEntityType(
  entityType?:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Purchase
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.SellerEvent,
  includeInReport?: boolean
) {
  const enabled = entityType != null;

  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();
  const { addStopGap } = useStopwatchLoggingContext();
  const hasLogSectionalDataFeature = useUserHasFeature(
    Feature.LogSectionalData
  );

  const queryClient = useQueryClient();

  const sharedQueryKey = [
    'TagClient.getTagsByEntityTypeId',
    activeAccountWebClientConfig.activeAccountId,
  ];

  const mainQueryKey = [...sharedQueryKey, entityType, enabled];

  const shouldQuery =
    activeAccountWebClientConfig.activeAccountId != null && enabled;

  const tagByEntityTypeQuery = useQuery({
    queryKey: [...mainQueryKey, includeInReport],
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }

      return new TagClient(activeAccountWebClientConfig).getTagsByEntityTypeId(
        entityType,
        includeInReport ?? null
      );
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    networkMode: 'offlineFirst',
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('TagClient.getTagsByEntityTypeId', error, {
          entityType,
          includeInReport,
        });
      },
    },
  });

  useEffect(() => {
    if (
      !tagByEntityTypeQuery.isLoading &&
      tagByEntityTypeQuery.data &&
      hasLogSectionalDataFeature &&
      entityType == ActionOutboxEntityType.Sale
    ) {
      const posEntityId = getDeepLinkIdFromUrl(
        SaleDeeplinkQueryParam,
        window.location.href
      );

      if (posEntityId == '') {
        return;
      }

      const logKey = `ActivePosEntity-${entityType}-${posEntityId}`;

      addStopGap(
        logKey,
        getPosEntityStopwatchLoggingId(entityType),
        getStopwatchGapsForActivePosEntityPageLoad(
          entityType as ActionOutboxEntityType
        ),
        StopwatchLoggingEventName.EntityDataLoad,
        entityType
      );
    }
  }, [
    tagByEntityTypeQuery.isLoading,
    tagByEntityTypeQuery.data,
    entityType,
    addStopGap,
    hasLogSectionalDataFeature,
  ]);

  const tagWithValueByEntityTypeQuery = useQuery({
    queryKey: [
      'TagClient.getTagsWithValueByEntityTypeId',
      activeAccountWebClientConfig.activeAccountId,
      entityType,
    ],
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }
      return new TagClient(
        activeAccountWebClientConfig
      ).getTagsWithValueByEntityTypeId(entityType);
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('TagClient.getTagsWithValueByEntityTypeId', error, {
          entityType,
        });
      },
    },
  });

  const isTag = useCallback(
    (name: string) => {
      return tagByEntityTypeQuery.data?.find((t) => t.key === name) != null;
    },
    [tagByEntityTypeQuery.data]
  );

  const mutation = useMutation({
    mutationFn: async ({ tags }: { tags: Tag[] }) => {
      return await new TagClient(
        activeAccountWebClientConfig
      ).upsertEntityTagsMetadata(entityType, tags);
    },
    // optimistic ui
    // Reference: https://tanstack.com/query/v3/docs/react/guides/optimistic-updates
    onMutate: ({ tags }) => {
      queryClient.cancelQueries({ queryKey: mainQueryKey });
      const prevValue = queryClient.getQueryData(mainQueryKey);
      queryClient.setQueryData(mainQueryKey, tags);
      return { prevValue };
    },
    onError: (err: ErrorTypes, { tags }, context) => {
      queryClient.setQueryData(mainQueryKey, context?.prevValue);
      showErrorDialog('TagClient.upsertEntityTagsMetadata', err, {
        trackErrorData: { tags },
      });
    },
    onSettled: () => {
      // Likely updating persistent tags, so we need to invalidate the shared query key across all entity types
      queryClient.invalidateQueries({ queryKey: sharedQueryKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ tags }: { tags: Tag[] }) => {
      return await new TagClient(
        activeAccountWebClientConfig
      ).deleteEntityTagsMetadata(
        entityType,
        tags.map((t) => t.key)
      );
    },
    // optimistic ui
    // Reference: https://tanstack.com/query/v3/docs/react/guides/optimistic-updates
    onMutate: ({ tags }) => {
      queryClient.cancelQueries({ queryKey: mainQueryKey });
      const prevValue = queryClient.getQueryData<Tag[]>(mainQueryKey);
      queryClient.setQueryData(
        mainQueryKey,
        prevValue?.filter((t) => !tags.includes(t))
      );
      return { prevValue };
    },
    onError: (err: ErrorTypes, { tags }, context) => {
      queryClient.setQueryData(mainQueryKey, context?.prevValue);
      showErrorDialog('TagClient.deleteEntityTagsMetadata', err, {
        trackErrorData: { tags },
      });
    },
    onSettled: () => {
      // Likely deleting persistent tags, so we need to invalidate the shared query key across all entity types
      queryClient.invalidateQueries({ queryKey: sharedQueryKey });
    },
  });

  const tagsWithNoValue = useMemo(() => {
    if (
      tagByEntityTypeQuery.data == null ||
      tagWithValueByEntityTypeQuery.data == null
    ) {
      return [];
    }

    return tagByEntityTypeQuery.data.filter((t) => {
      const tagWithValue = tagWithValueByEntityTypeQuery.data?.find(
        (t2) => t2.key === t.key
      );
      return tagWithValue == null;
    });
  }, [tagByEntityTypeQuery.data, tagWithValueByEntityTypeQuery.data]);

  return {
    tagsMetadata: tagByEntityTypeQuery.data,
    isLoading: tagByEntityTypeQuery.isLoading,
    tagsMetadataNumeric: tagByEntityTypeQuery.data?.filter(
      (t) =>
        t.valueType === TagsValueType.Decimal ||
        t.valueType === TagsValueType.Int
    ),
    tagsWithNoValue,
    isError: tagByEntityTypeQuery.isError,
    refetch: tagByEntityTypeQuery.refetch,
    async mergeEntityTagsMetadata(tagsToUpdate: Tag[]) {
      await mutation.mutateAsync({ tags: tagsToUpdate });
    },
    async deleteEntityTagsMetadata(tagsToDelete: Tag[]) {
      await deleteMutation.mutateAsync({ tags: tagsToDelete });
    },
    isTag,
  };
}
